import React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  Typography,
  Container,
  Tooltip,
} from "@mui/material";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ExploreOutlinedIcon from "@mui/icons-material/ExploreOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { useToast } from "../hooks/useToast";
import api from "../api";

type PrimaryNav = "discover" | "chats" | null;

const Navigation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { showToast } = useToast();

  const isProfileComplete = !!(
    user?.photo_url &&
    user?.voice_recording_url &&
    user?.location
  );

  const onConversationsPage = location.pathname.startsWith("/conversations");

  const { data: navStatus } = useQuery({
    queryKey: ['conversations', 'status'],
    queryFn: () => api.get("/api/conversations/status").then(r => r.data),
    enabled: isProfileComplete && !onConversationsPage,
  });

  const primaryNav: PrimaryNav = !isProfileComplete
    ? null
    : onConversationsPage
      ? "chats"
      : navStatus
        ? (navStatus.has_any_history ? "chats" : "discover")
        : null;

  const handleRefer = async () => {
    const url = "https://know-yourself.dateseriously.com";
    const title = "Seriously — Know Yourself";
    const text =
      "Discover your unconscious dating biases in 3 minutes and find your person on Seriously:";

    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
      } catch {
        // user cancelled
      }
    } else {
      await navigator.clipboard.writeText(`${text}\n${url}`);
      showToast("Referral link copied to clipboard!");
    }
  };

  const isActive = (path: string) => location.pathname.startsWith(path);
  const kyUrl = "https://know-yourself.dateseriously.com?return=" + encodeURIComponent(window.location.origin + "/discover");

  return (
    <>
      {/* ── Main nav bar ───────────────────────────────────────────────── */}
      <Box sx={{
        borderBottom: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.default",
        position: "sticky",
        top: 0,
        zIndex: 50,
      }}>
        <Container maxWidth="sm">
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1 }}>
            {/* Logo */}
            <Box
              onClick={() => navigate("/")}
              sx={{ display: "flex", alignItems: "center", gap: 0.8, cursor: "pointer", flexShrink: 0, "&:hover .logo-text": { color: "primary.main" } }}
            >
              <FavoriteIcon sx={{ fontSize: 16, color: "primary.main" }} />
              <Typography
                className="logo-text"
                sx={{
                  fontFamily: "'Libre Baskerville', Georgia, serif",
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  color: "text.primary",
                  letterSpacing: "-0.02em",
                  transition: "color 0.2s",
                }}
              >
                Seriously
              </Typography>
            </Box>

            {/* Nav items */}
            <Box sx={{ display: "flex", gap: { xs: 0.3, sm: 0.5 }, alignItems: "center" }}>
              {primaryNav === "discover" && (
                <NavBtn
                  icon={<ExploreOutlinedIcon sx={{ fontSize: 14 }} />}
                  label="Discover"
                  active={isActive("/discover")}
                  onClick={() => navigate("/discover")}
                />
              )}
              {primaryNav === "chats" && (
                <NavBtn
                  icon={<ChatBubbleOutlineIcon sx={{ fontSize: 14 }} />}
                  label="Chats"
                  active={isActive("/conversations")}
                  onClick={() => navigate("/conversations")}
                />
              )}
              <NavBtn
                icon={<PsychologyOutlinedIcon sx={{ fontSize: 14 }} />}
                label="Know Yourself"
                onClick={() => { window.location.href = kyUrl; }}
              />
              <NavBtn
                icon={<PersonOutlineIcon sx={{ fontSize: 14 }} />}
                label="Profile"
                active={isActive("/profile")}
                onClick={() => navigate("/profile")}
              />
              <Box sx={{ width: "1px", height: 14, backgroundColor: "divider", mx: { xs: 0.2, sm: 0.5 } }} />
              <Tooltip title="Refer a friend" arrow>
                <Box
                  onClick={handleRefer}
                  sx={{
                    cursor: "pointer", p: 0.5, borderRadius: "3px",
                    display: "flex", alignItems: "center", gap: 0.3,
                    color: "text.disabled",
                    transition: "all 0.2s",
                    "&:hover": { color: "primary.main" },
                  }}
                >
                  <PersonAddOutlinedIcon sx={{ fontSize: 14 }} />
                </Box>
              </Tooltip>
              <Tooltip title="Manifesto" arrow>
                <Box
                  onClick={() => navigate("/manifesto")}
                  sx={{
                    cursor: "pointer", p: 0.5, borderRadius: "3px",
                    display: "flex", alignItems: "center",
                    color: "text.disabled",
                    transition: "all 0.2s",
                    "&:hover": { color: "primary.main" },
                  }}
                >
                  <MenuBookOutlinedIcon sx={{ fontSize: 14 }} />
                </Box>
              </Tooltip>
              <Tooltip title="Feedback" arrow>
                <Box
                  onClick={() => navigate("/product-feedback")}
                  sx={{
                    cursor: "pointer", p: 0.5, borderRadius: "3px",
                    display: "flex", alignItems: "center",
                    color: "text.disabled",
                    transition: "all 0.2s",
                    "&:hover": { color: "primary.main" },
                  }}
                >
                  <RateReviewOutlinedIcon sx={{ fontSize: 14 }} />
                </Box>
              </Tooltip>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ── Pre-launch banner ──────────────────────────────────────────── */}
      {user && !user.launched && (
        <Box sx={{ backgroundColor: "warning.light", borderBottom: "1px solid", borderColor: "warning.main", px: 2, py: 0.8 }}>
          <Container maxWidth="sm">
            <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.72rem", color: "#92400E", textAlign: "center" }}>
              <strong>Pre-launch</strong> — Create your profile to secure your place. We'll text you when we go live.
            </Typography>
          </Container>
        </Box>
      )}
    </>
  );
};

function NavBtn({ icon, label, active, onClick }: {
  icon: React.ReactNode; label: string; active?: boolean; onClick: () => void;
}) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex", alignItems: "center", gap: 0.4,
        px: { xs: 0.6, sm: 0.8 }, py: 0.4,
        cursor: "pointer", borderRadius: "3px",
        border: `1px solid ${active ? "#1a1614" : "transparent"}`,
        backgroundColor: active ? "#1a1614" : "transparent",
        color: active ? "#faf8f5" : "#9a918e",
        transition: "all 0.2s",
        "&:hover": { color: active ? "#faf8f5" : "#c0392b" },
      }}
    >
      {icon}
      <Typography sx={{ fontFamily: "'DM Sans'", fontSize: { xs: "0.55rem", sm: "0.6rem" }, fontWeight: 500, letterSpacing: "0.04em", display: { xs: "none", sm: "block" } }}>
        {label}
      </Typography>
    </Box>
  );
}

export default Navigation;
